export const CARD_PREVIEW_WIDTH = 520;
export const CARD_PREVIEW_HEIGHT = 328;
export const CARD_BORDER_RADIUS = 18;
export const CARD_ASPECT_RATIO = CARD_PREVIEW_WIDTH / CARD_PREVIEW_HEIGHT;

export const CARD_INSET_HORIZONTAL = 37;
export const CARD_INSET_VERTICAL = 34;

export const CARD_INSTITUTION_LOGO_WIDTH = 125;
export const CARD_INSTITUTION_LOGO_MAX_HEIGHT = 43;

export const CARD_NETWORK_LOGO_WIDTH = 98;
export const CARD_NETWORK_LOGO_HEIGHT = Math.round(CARD_NETWORK_LOGO_WIDTH * (84 / 98));
export const CARD_NETWORK_LOGO_INSET_RIGHT = 10;
export const CARD_NETWORK_LOGO_INSET_BOTTOM = 10;

export const CARD_CUSTOM_LOGO_MAX_FILE_SIZE = 500 * 1024;

export const CARD_LOGO_ACCEPTED_TYPES = [
  "image/png",
  "image/svg+xml",
  "image/jpeg",
  "image/jpg",
  "image/webp",
];

/** Same size limit as logo (console UI); API allows up to 1 MB decoded. */
export const CARD_ART_MAX_FILE_SIZE = CARD_CUSTOM_LOGO_MAX_FILE_SIZE;

export const CARD_DESIGN_COLOR_PRESETS = [
  { id: "black", value: "#000000" },
  { id: "navy", value: "#0A1628" },
  { id: "gold", value: "#C9A227" },
  { id: "white", value: "#FFFFFF" },
];

export const CARD_NETWORK_CHOICES = [
  { id: "mastercard", available: true },
];

/** Processor catalog codes (Black `033101`, Gold `033501`). */
export const MASTERCARD_PS_PRODUCT_CODES = {
  black: "033101",
  gold: "033501",
};

/** Default program processor route (API code). UI shows brand + neutral route label. */
export const DEFAULT_PROCESSOR_CODE = "PAYSMART_BR";
export const DEFAULT_PROCESSOR_BRAND = "Valut Matrix";
export const DEFAULT_PROCESSOR_ROUTE_LABEL = "Roteamento Brasil";

export const MASTERCARD_VARIANTS = {
  black: {
    id: "black",
    backgroundColor: "#000000",
    productType: "debit_credit",
    psProductCode: MASTERCARD_PS_PRODUCT_CODES.black,
  },
  gold: {
    id: "gold",
    backgroundColor: "#000000",
    productType: "prepaid_voucher",
    psProductCode: MASTERCARD_PS_PRODUCT_CODES.gold,
  },
};

export const resolveMastercardVariantFromPsProductCode = (psProductCode) => {
  const code = String(psProductCode || "").trim();
  if (code === MASTERCARD_PS_PRODUCT_CODES.gold) return "gold";
  return "black";
};

export const getPsProductCodeForDesign = (design) => {
  const variantId =
    design?.network === "mastercard"
      ? design?.mastercardVariant || "black"
      : "black";
  return (
    MASTERCARD_VARIANTS[variantId]?.psProductCode || MASTERCARD_PS_PRODUCT_CODES.black
  );
};

export const DEFAULT_INVOICE_DAY_OPTIONS = [5, 15, 25];

/** Liquidação fixa para produto débito/crédito (Mastercard Black). */
export const DEBIT_CREDIT_SETTLEMENT_LAGS = [0, 19];

export const DEBIT_CREDIT_GUARANTEE_PERCENT = 100;

export const formatSettlementLags = (lags = []) => lags.map((day) => `D+${day}`).join(", ");

/** @deprecated use CARD_NETWORK_CHOICES */
export const CARD_NETWORK_OPTIONS = ["mastercard"];

export const getMastercardVariantConfig = (variantId = "black") =>
  MASTERCARD_VARIANTS[variantId] ?? MASTERCARD_VARIANTS.black;

export const isMastercardBlackVariant = (design) =>
  design?.network === "mastercard" && design?.mastercardVariant === "black";

export const isMastercardGoldVariant = (design) =>
  design?.network === "mastercard" && design?.mastercardVariant === "gold";

export const DEFAULT_CARDHOLDER_PREVIEW_NAME = "NOME SOBRENOME";

export const DEFAULT_CARD_BACK_WEBSITE = "seusite.com";
export const DEFAULT_CARD_BACK_PHONE_METROPOLITAN = "4000 0000";
export const DEFAULT_CARD_BACK_PHONE_OTHER = "0800 000 0002";
export const DEFAULT_CARD_NUMBER_PREVIEW = "•••• •••• •••• 1234";
export const DEFAULT_EXPIRY_PREVIEW = "12/28";
export const DEFAULT_CVC_PREVIEW = "•••";
export const DEFAULT_ISSUER_NAME = "Issuer Instituição de Pagamento LTDA";

export const buildIssuerLegalText = (
  issuerName = DEFAULT_ISSUER_NAME,
  network = "mastercard",
) => {
  const licenseBy =
    network === "mastercard"
      ? "Mastercard® International"
      : network === "visa"
        ? "Visa International"
        : "the card network";

  return `This card is issue by ${issuerName}, pursuant to license by ${licenseBy}`;
};

export const DEFAULT_PRODUCT_DESIGN = {
  name: "",
  backgroundColor: "#000000",
  cardholderPreviewName: DEFAULT_CARDHOLDER_PREVIEW_NAME,
  customLogoUrl: null,
  frontArtUrl: null,
  backArtUrl: null,
  network: "mastercard",
  mastercardVariant: "black",
  productType: "debit_credit",
  status: "draft",
  website: DEFAULT_CARD_BACK_WEBSITE,
  phoneMetropolitan: DEFAULT_CARD_BACK_PHONE_METROPOLITAN,
  phoneOther: DEFAULT_CARD_BACK_PHONE_OTHER,
  cardNumberPreview: DEFAULT_CARD_NUMBER_PREVIEW,
  expiryPreview: DEFAULT_EXPIRY_PREVIEW,
  cvcPreview: DEFAULT_CVC_PREVIEW,
};

export const FUNDING_MODE_PIX_KEY = "pix_key";
export const FUNDING_MODE_LINKED_ACCOUNT = "linked_account";

export const ISSUANCE_ENTITY_PF = "pf";
export const ISSUANCE_ENTITY_PJ = "pj";
export const ISSUANCE_ENTITY_BOTH = "both";

export const DEFAULT_CARD_PROGRAM_CONFIG = {
  programKind: "NATIVE",
  processorCode: DEFAULT_PROCESSOR_CODE,
  authorizerClientUrl: "",
  authorizerPublicKey: "",
  invoiceDays: [5, 15, 25],
  settlementDays: DEBIT_CREDIT_SETTLEMENT_LAGS,
  guaranteePercent: DEBIT_CREDIT_GUARANTEE_PERCENT,
  webhookUrl: "",
  fundingMode: FUNDING_MODE_LINKED_ACCOUNT,
  fundingAccountId: "",
  issuanceEntityType: ISSUANCE_ENTITY_PF,
};

export const PROGRAM_KIND_NATIVE = "NATIVE";
export const PROGRAM_KIND_COBRANDED = "COBRANDED";

export const isCobrandedProgramKind = (programKind) =>
  String(programKind || "").trim().toUpperCase() === PROGRAM_KIND_COBRANDED;

export const isLightBackground = (color) => {
  if (!color) {
    return false;
  }

  const hex = color.replace("#", "");
  if (hex.length !== 6) {
    return false;
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.6;
};

export const getContrastTextColor = (
  backgroundColor,
  { light = "#1a1a1a", dark = "#ffffff" } = {},
) => (isLightBackground(backgroundColor) ? light : dark);

export const validateCardLogoFile = (file) => {
  if (!file) {
    return { valid: false, reason: "noFile" };
  }

  if (!CARD_LOGO_ACCEPTED_TYPES.includes(file.type)) {
    return { valid: false, reason: "invalidType" };
  }

  if (file.size > CARD_CUSTOM_LOGO_MAX_FILE_SIZE) {
    return { valid: false, reason: "fileTooLarge" };
  }

  return { valid: true };
};

/** Alias — same rules as logo (png/jpeg/svg/webp ≤ 500 KB in console). */
export const validateCardArtFile = validateCardLogoFile;
